import React, { useMemo, useState } from 'react'
import { MdAdd, MdCheckCircle } from 'react-icons/md'
import type { PriceListItem } from '@/types'
import '@/styles/components/product-browser.css'

const PRODUCT_IMAGE = '/milk-bottle.jpg'

const formatPrice = (price: number): string => `£${price.toFixed(2)}`

/** The "no category chosen" entry. */
const ALL_CATEGORIES = 'All products'

/** What the customer already gets of a product, day by day. */
export interface OrderedDays {
  day: number
  quantity: number
}

interface ProductBrowserProps {
  items: PriceListItem[]
  /** Per product id, the days it is already on. Drives the status line. */
  orderedByProduct?: Map<string, OrderedDays[]>
  /**
   * False when the product cannot reach any of the customer's delivery days.
   * Those stay visible but dimmed: hiding them makes the catalogue lie about
   * itself, and the detail dialog explains why they are out of reach.
   */
  isOrderable?: (item: PriceListItem) => boolean
  /** The card's action. Omitted gives a browse-only grid. */
  actionLabel?: (item: PriceListItem) => string
  onAction?: (item: PriceListItem) => void
  /** Opening the packshot, which is how product detail is reached everywhere. */
  onViewDetail: (item: PriceListItem) => void
  emptyMessage?: string
}

/**
 * Search, categories and a grid of product cards.
 *
 * Shared by the Products page and the add-product dialog, which were built
 * separately and had drifted into two different-looking versions of the same
 * screen. The only real difference is what the card's button does, so that is
 * a prop and everything else is common.
 */
const ProductBrowser: React.FC<ProductBrowserProps> = ({
  items,
  orderedByProduct,
  isOrderable,
  actionLabel,
  onAction,
  onViewDetail,
  emptyMessage = 'No products found.',
}) => {
  const [search, setSearch] = useState('')
  const [category, setCategory] = useState<string>(ALL_CATEGORIES)

  const productName = (item: PriceListItem): string =>
    item.name || item.description || item.product_number

  /** The distinct Woo categories present in the list being shown. */
  const categories = useMemo(() => {
    const names = new Set<string>()

    items.forEach(item => {
      (item.categories ?? []).forEach(c => names.add(c.name))
    })

    return [
      ALL_CATEGORIES,
      ...Array.from(names).sort((a, b) => a.localeCompare(b)),
    ]
  }, [items])

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase()

    return items.filter(item => {
      if (category !== ALL_CATEGORIES && !(item.categories ?? []).some(c => c.name === category)) {
        return false
      }
      if (!q) return true

      return item.product_number.toLowerCase().includes(q)
        || (item.name ?? '').toLowerCase().includes(q)
        || (item.description ?? '').toLowerCase().includes(q)
    })
  }, [items, category, search])

  return (
    <div className="product-browser">
      <input
        type="search"
        className="product-browser-search"
        placeholder="Search products..."
        value={search}
        onChange={e => setSearch(e.target.value)}
      />

      <div className="product-browser-body">
        {categories.length > 1 && (
          <aside className="product-browser-categories" aria-label="Filter by category">
            {categories.map(cat => (
              <button
                key={cat}
                type="button"
                className={`product-browser-category${category === cat ? ' active' : ''}`}
                onClick={() => setCategory(cat)}
                aria-pressed={category === cat}
              >
                <span className="product-browser-category-name">{cat}</span>
              </button>
            ))}
          </aside>
        )}

        {visible.length === 0 ? (
          <p className="product-browser-empty">{emptyMessage}</p>
        ) : (
          <div className="product-browser-grid">
            {visible.map(item => {
              const ordered = orderedByProduct?.get(item.product_id)
              const orderable = isOrderable ? isOrderable(item) : true
              // DF-286: a stock-managed one-off (the Milk Locker) that has sold
              // out. Flagged on the card; the Add still opens its landing page,
              // where the back-in-stock sign-up is offered.
              const outOfStock = item.stock_managed === true && item.in_stock === false

              return (
                <div
                  key={item.id}
                  className={`product-card${orderable ? '' : ' is-unavailable'}${ordered && ordered.length > 0 ? ' is-ordered' : ''}${outOfStock ? ' is-out-of-stock' : ''}`}
                >
                  {/* The packshot opens the detail, as it does on the weekly
                      panel and in the dialogs. */}
                  <button
                    type="button"
                    className="product-card-thumb"
                    onClick={() => onViewDetail(item)}
                    aria-label={`About ${productName(item)}`}
                  >
                    <img
                      className="product-card-image"
                      src={item.image_url || PRODUCT_IMAGE}
                      alt=""
                      onError={e => { e.currentTarget.src = PRODUCT_IMAGE }}
                    />
                  </button>

                  <div className="product-card-text">
                    <span className="product-card-name">{productName(item)}</span>
                    <span className="product-card-price">
                      {formatPrice(item.amount_inc_vat ?? item.amount)}
                    </span>

                    {outOfStock ? (
                      <span className="product-card-status is-muted">
                        Out of stock
                      </span>
                    ) : ordered && ordered.length > 0 ? (
                      <span className="product-card-status">
                        <MdCheckCircle aria-hidden="true" />
                        You get this product delivered
                      </span>
                    ) : !orderable ? (
                      <span className="product-card-status is-muted">
                        Not on your delivery days
                      </span>
                    ) : null}
                  </div>

                  {actionLabel && onAction && (
                    <button
                      type="button"
                      className="product-card-action"
                      onClick={() => onAction(item)}
                      disabled={!orderable}
                    >
                      <MdAdd aria-hidden="true" />
                      {actionLabel(item)}
                    </button>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

export default ProductBrowser
